import React from 'react';

/**
 * Jungle border strips for left/right edges of white sections.
 * Narrower, varied colors, stays behind content. Peeks from the edge.
 */

/* Varied green palette for natural look */
const G1 = '#0D3B0F'; // very dark
const G2 = '#1B5E20'; // deep jungle
const G3 = '#2E7D32'; // mid green
const G4 = '#388E3C'; // emerald
const G5 = '#4CAF50'; // fern
const G6 = '#66BB6A'; // light green
const G7 = '#81C784'; // pale green
const G8 = '#2D5F2E'; // olive
const G9 = '#1A6B3C'; // teal-green

/* ─── Leaf SVGs — each a different shape & color combo ─── */

const PalmA = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} width="140" height="220" viewBox="0 0 140 220" fill="none">
    <path d="M70 220 C68 180 66 140 70 100 C74 60 70 15 68 0" stroke={G1} strokeWidth="3" />
    <path d="M66 25 C45 18 12 28 2 48 C20 34 46 26 66 36" fill={G2} />
    <path d="M64 55 C38 44 6 56 0 82 C18 64 42 54 64 66" fill={G5} />
    <path d="M65 88 C40 76 8 88 0 116 C18 96 44 86 65 98" fill={G3} />
    <path d="M66 120 C44 110 16 118 6 142 C22 126 48 118 66 128" fill={G6} />
    <path d="M74 25 C95 18 128 28 138 48 C120 34 94 26 74 36" fill={G2} />
    <path d="M76 55 C102 44 134 56 140 82 C122 64 98 54 76 66" fill={G4} />
    <path d="M75 88 C100 76 132 88 140 116 C122 96 96 86 75 98" fill={G8} />
    <path d="M74 120 C96 110 124 118 134 142 C118 126 92 118 74 128" fill={G7} />
  </svg>
);

const PalmB = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} width="120" height="200" viewBox="0 0 120 200" fill="none">
    <path d="M60 200 C58 160 56 120 60 80 C64 40 60 10 58 0" stroke={G1} strokeWidth="2.5" />
    <path d="M56 20 C35 14 8 24 0 44 C16 30 38 22 56 30" fill={G9} />
    <path d="M55 50 C30 40 4 52 0 78 C16 60 36 50 55 62" fill={G3} />
    <path d="M56 82 C34 72 8 82 0 108 C16 90 38 80 56 92" fill={G6} />
    <path d="M64 20 C85 14 112 24 120 44 C104 30 82 22 64 30" fill={G8} />
    <path d="M65 50 C90 40 116 52 120 78 C104 60 84 50 65 62" fill={G4} />
    <path d="M64 82 C86 72 112 82 120 108 C104 90 82 80 64 92" fill={G5} />
  </svg>
);

const MonsteraA = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} width="130" height="140" viewBox="0 0 130 140" fill="none">
    <path d="M65 140 C64 120 62 100 65 80" stroke={G1} strokeWidth="3" />
    <path d="M65 10 C38 10 10 32 6 60 C3 85 16 108 40 116 C50 118 58 114 65 104 C72 114 80 118 90 116 C114 108 127 85 124 60 C120 32 92 10 65 10Z" fill={G2} />
    <ellipse cx="35" cy="55" rx="10" ry="14" fill="#FFF5E6" opacity="0.1" />
    <ellipse cx="95" cy="55" rx="10" ry="14" fill="#FFF5E6" opacity="0.1" />
    <ellipse cx="48" cy="85" rx="7" ry="10" fill="#FFF5E6" opacity="0.08" />
    <ellipse cx="82" cy="85" rx="7" ry="10" fill="#FFF5E6" opacity="0.08" />
    <path d="M65 16 L65 102" stroke={G1} strokeWidth="1.5" opacity="0.3" />
    <path d="M65 35 L35 48" stroke={G1} strokeWidth="1" opacity="0.2" />
    <path d="M65 35 L95 48" stroke={G1} strokeWidth="1" opacity="0.2" />
    <path d="M65 60 L28 75" stroke={G1} strokeWidth="1" opacity="0.2" />
    <path d="M65 60 L102 75" stroke={G1} strokeWidth="1" opacity="0.2" />
  </svg>
);

const MonsteraB = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} width="110" height="120" viewBox="0 0 110 120" fill="none">
    <path d="M55 120 C54 105 52 88 55 70" stroke={G1} strokeWidth="2.5" />
    <path d="M55 8 C32 8 8 26 5 50 C2 72 14 92 34 98 C42 100 48 96 55 88 C62 96 68 100 76 98 C96 92 108 72 105 50 C102 26 78 8 55 8Z" fill={G9} />
    <ellipse cx="30" cy="46" rx="8" ry="11" fill="#FFF5E6" opacity="0.1" />
    <ellipse cx="80" cy="46" rx="8" ry="11" fill="#FFF5E6" opacity="0.1" />
    <path d="M55 14 L55 86" stroke={G1} strokeWidth="1.2" opacity="0.25" />
    <path d="M55 30 L30 40" stroke={G1} strokeWidth="0.8" opacity="0.2" />
    <path d="M55 30 L80 40" stroke={G1} strokeWidth="0.8" opacity="0.2" />
  </svg>
);

const BroadA = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} width="110" height="130" viewBox="0 0 110 130" fill="none">
    <path d="M55 130 C55 112 54 96 55 78" stroke={G1} strokeWidth="2.5" />
    <path d="M55 5 C28 5 4 28 2 54 C0 78 14 100 38 110 C46 112 52 108 55 78 C58 108 64 112 72 110 C96 100 110 78 108 54 C106 28 82 5 55 5Z" fill={G3} />
    <path d="M55 10 L55 105" stroke={G1} strokeWidth="1" opacity="0.2" />
    <path d="M55 30 L22 44" stroke={G1} strokeWidth="0.8" opacity="0.15" />
    <path d="M55 30 L88 44" stroke={G1} strokeWidth="0.8" opacity="0.15" />
    <path d="M55 55 L16 72" stroke={G1} strokeWidth="0.8" opacity="0.15" />
    <path d="M55 55 L94 72" stroke={G1} strokeWidth="0.8" opacity="0.15" />
  </svg>
);

const BroadB = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} width="100" height="120" viewBox="0 0 100 120" fill="none">
    <path d="M50 120 C50 105 49 90 50 72" stroke={G1} strokeWidth="2" />
    <path d="M50 4 C26 4 4 24 2 48 C0 70 12 90 34 100 C42 102 48 98 50 72 C52 98 58 102 66 100 C88 90 100 70 98 48 C96 24 74 4 50 4Z" fill={G6} />
    <path d="M50 8 L50 96" stroke={G1} strokeWidth="0.8" opacity="0.2" />
    <path d="M50 28 L20 40" stroke={G1} strokeWidth="0.6" opacity="0.15" />
    <path d="M50 28 L80 40" stroke={G1} strokeWidth="0.6" opacity="0.15" />
  </svg>
);

const FernA = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} width="50" height="160" viewBox="0 0 50 160" fill="none">
    <path d="M25 160 C25 130 24 100 25 70 C26 40 25 15 25 2" stroke={G5} strokeWidth="2" />
    <path d="M25 10 C18 6 10 8 5 16 C12 10 20 10 25 18" fill={G5} />
    <path d="M25 10 C32 6 40 8 45 16 C38 10 30 10 25 18" fill={G7} />
    <path d="M25 30 C17 24 8 26 3 36 C10 28 20 28 25 38" fill={G4} />
    <path d="M25 30 C33 24 42 26 47 36 C40 28 30 28 25 38" fill={G6} />
    <path d="M25 52 C17 46 8 48 3 58 C10 50 20 50 25 60" fill={G5} />
    <path d="M25 52 C33 46 42 48 47 58 C40 50 30 50 25 60" fill={G3} />
    <path d="M25 76 C18 70 10 72 5 82 C12 74 20 74 25 84" fill={G4} opacity="0.8" />
    <path d="M25 76 C32 70 40 72 45 82 C38 74 30 74 25 84" fill={G7} opacity="0.8" />
    <path d="M25 100 C19 94 12 96 7 106 C14 98 21 98 25 108" fill={G5} opacity="0.6" />
    <path d="M25 100 C31 94 38 96 43 106 C36 98 29 98 25 108" fill={G6} opacity="0.6" />
  </svg>
);

const VineA = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} width="24" height="180" viewBox="0 0 24 180" fill="none">
    <path d="M12 0 C8 20 16 40 10 60 C4 80 18 100 10 120 C4 140 16 160 12 180" stroke={G4} strokeWidth="2" fill="none" />
    <path d="M10 35 C6 30 2 32 1 38 C5 34 9 34 10 40" fill={G5} />
    <path d="M12 75 C16 70 20 72 21 78 C17 74 13 74 12 80" fill={G7} />
    <path d="M10 115 C6 110 2 112 1 118 C5 114 9 114 10 120" fill={G6} />
    <path d="M12 155 C16 150 20 152 21 158 C17 154 13 154 12 160" fill={G5} />
  </svg>
);

interface LeafPlacement {
  Component: React.FC<{ style?: React.CSSProperties }>;
  top: string;
  offset: number;
  rotate: number;
  scale: number;
}

const Leaf = ({ Component, top, offset, rotate, scale, isLeft }: LeafPlacement & { isLeft: boolean }) => (
  <Component
    style={{
      position: 'absolute',
      top,
      [isLeft ? 'left' : 'right']: offset,
      transform: `${!isLeft ? 'scaleX(-1) ' : ''}rotate(${rotate}deg) scale(${scale})`,
    }}
  />
);

export const JungleBorder = ({ side, className = '' }: { side: 'left' | 'right'; className?: string }) => {
  const isLeft = side === 'left';

  return (
    <div
      className={`absolute top-0 ${isLeft ? 'left-0' : 'right-0'} h-full pointer-events-none overflow-hidden hidden md:block ${className}`}
      style={{ width: 'clamp(60px, 8vw, 140px)', zIndex: 0 }}
    >
      {/* Palm fronds — varied, pulled to edge */}
      <Leaf Component={PalmA} top="-2%" offset={-60} rotate={8} scale={0.9} isLeft={isLeft} />
      <Leaf Component={PalmB} top="22%" offset={-55} rotate={-12} scale={0.85} isLeft={isLeft} />
      <Leaf Component={PalmA} top="48%" offset={-65} rotate={15} scale={0.8} isLeft={isLeft} />
      <Leaf Component={PalmB} top="72%" offset={-58} rotate={-8} scale={0.9} isLeft={isLeft} />

      {/* Monstera — two different shapes */}
      <Leaf Component={MonsteraA} top="8%" offset={-30} rotate={20} scale={0.85} isLeft={isLeft} />
      <Leaf Component={MonsteraB} top="38%" offset={-25} rotate={-18} scale={0.9} isLeft={isLeft} />
      <Leaf Component={MonsteraA} top="62%" offset={-35} rotate={14} scale={0.8} isLeft={isLeft} />
      <Leaf Component={MonsteraB} top="85%" offset={-28} rotate={-12} scale={0.85} isLeft={isLeft} />

      {/* Broad leaves — two color variants */}
      <Leaf Component={BroadA} top="5%" offset={-20} rotate={28} scale={0.85} isLeft={isLeft} />
      <Leaf Component={BroadB} top="30%" offset={-15} rotate={-32} scale={0.9} isLeft={isLeft} />
      <Leaf Component={BroadA} top="55%" offset={-22} rotate={22} scale={0.8} isLeft={isLeft} />
      <Leaf Component={BroadB} top="78%" offset={-18} rotate={-26} scale={0.85} isLeft={isLeft} />

      {/* Ferns */}
      <Leaf Component={FernA} top="0%" offset={-8} rotate={25} scale={0.8} isLeft={isLeft} />
      <Leaf Component={FernA} top="25%" offset={-12} rotate={-20} scale={0.85} isLeft={isLeft} />
      <Leaf Component={FernA} top="50%" offset={-6} rotate={30} scale={0.75} isLeft={isLeft} />
      <Leaf Component={FernA} top="75%" offset={-10} rotate={-22} scale={0.8} isLeft={isLeft} />

      {/* Vines */}
      <Leaf Component={VineA} top="0%" offset={5} rotate={2} scale={0.9} isLeft={isLeft} />
      <Leaf Component={VineA} top="35%" offset={0} rotate={-3} scale={0.85} isLeft={isLeft} />
      <Leaf Component={VineA} top="65%" offset={8} rotate={4} scale={0.9} isLeft={isLeft} />

      {/* Soft gradient — leaves fade out naturally toward center */}
      <div
        className="absolute inset-0"
        style={{
          background: isLeft
            ? 'linear-gradient(to right, transparent 0%, transparent 15%, rgba(255,248,240,0.5) 45%, rgba(255,248,240,0.85) 70%, #FFF8F0 100%)'
            : 'linear-gradient(to left, transparent 0%, transparent 15%, rgba(255,248,240,0.5) 45%, rgba(255,248,240,0.85) 70%, #FFF8F0 100%)',
          zIndex: 1,
        }}
      />
    </div>
  );
};

export default JungleBorder;
